import { useCallback } from 'react';
import { useGetCatalogSongQuery, usePatchCatalogSongMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { useRealtimePollInterval } from '../../../realtime/useRealtimePollInterval';

/**
 * While the master is read, the sheet asks again every 5 s, so it turns ready on its own.
 * With the realtime socket connected, `catalog.song.updated` does that and the sheet does not poll.
 */
const PROCESSING_POLL_MS = 5000;

interface UseCatalogSongProps {
  accountId: number;
  /** Undefined skips the read (the sheet is closed). */
  itemUuid?: string;
}

/**
 * The song files of one catalog row (design f2): `GET .../catalog/:uuid/song`, and the edits of
 * the lyrics and the ISRC (`PATCH .../song`). `save` resolves true when the server took the edit.
 */
export function useCatalogSong({ accountId, itemUuid }: UseCatalogSongProps) {
  const query = useGetCatalogSongQuery(
    { accountId, itemUuid: itemUuid ?? '' },
    { skip: itemUuid === undefined, refetchOnMountOrArgChange: true }
  );
  const isProcessing = query.data?.song.master?.status === 'processing';
  // A second subscription with polling only while the master is read.
  const pollingInterval = useRealtimePollInterval(PROCESSING_POLL_MS);
  useGetCatalogSongQuery(
    { accountId, itemUuid: itemUuid ?? '' },
    { skip: itemUuid === undefined || !isProcessing, pollingInterval }
  );
  const [patchSong, patch] = usePatchCatalogSongMutation();

  const save = useCallback(
    async (fields: { lyrics?: string | null; isrc?: string | null }): Promise<boolean> => {
      if (itemUuid === undefined) {
        return false;
      }
      const result = await patchSong({ accountId, itemUuid, ...fields });
      return isSuccessfulResult(result);
    },
    [accountId, itemUuid, patchSong]
  );

  const retry = useCallback(() => {
    query.refetch().catch(console.error);
  }, [query]);

  return {
    song: query.data?.song,
    isLoading: query.isLoading,
    error: query.error,
    retry,
    save,
    isSaving: patch.isLoading,
    saveError: patch.error,
    resetSave: patch.reset,
  };
}
